import { useCallback, useEffect, useRef, useState } from 'react'
import { NO_GESTURE, begin, end, move, wasTap } from './gesture'
import type { Gesture } from './gesture'
import { fitView, openingView, revealBox, zoomAbout } from './treeLayout'

/**
 * Drag to pan, wheel or pinch to zoom, and a button that puts it all back.
 *
 * The decisions about *what a gesture is* live in `gesture.ts`, which is a pure
 * reducer with its own tests. This is the part that cannot be tested without a
 * browser: reading pointer events, holding the view, and asking the DOM how big
 * it is.
 *
 * Two things here are less obvious than they look:
 *
 *  - **The pointer is captured late, not on pointerdown.** Capturing retargets
 *    the compatibility mouse events to the capturing element, which means the
 *    `click` a browser synthesises lands on *this frame* instead of on the card
 *    the finger was actually on. Doing it eagerly made every node in the tree
 *    unclickable while leaving pan and zoom working perfectly. `gesture.ts`
 *    decides when a gesture has earned the capture; until then a tap is a tap
 *    and reaches whatever is under it.
 *  - **The wheel listener is attached by hand**, because React's `onWheel` is
 *    passive and cannot `preventDefault()`. Without that call, zooming the
 *    diagram scrolls the page behind it at the same time.
 */

export interface View {
  readonly scale: number
  readonly x: number
  readonly y: number
}

export interface PanZoom {
  readonly view: View
  readonly frameRef: (el: HTMLDivElement | null) => void
  readonly onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void
  readonly onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void
  readonly onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void
  /** True while a *drag* is in progress — for the grabbing cursor. Not true of
   *  a finger resting still, which is a tap in the making. */
  readonly panning: boolean
  /** Was the gesture that just ended a tap? Asked by a card before it opens. */
  readonly tapped: () => boolean
  readonly zoomBy: (factor: number) => void
  readonly fit: () => void
  /** Pan the least distance that brings this diagram-space box into view —
   *  less `insetRight` pixels of frame that something is floating over. */
  readonly reveal: (box: { x: number; y: number; width: number; height: number }, insetRight?: number) => void
}

export interface PanZoomOptions {
  /**
   * The view is reset to its opening position when this changes — a different
   * root, say — and *not* when the diagram merely changes size. Expanding a
   * branch grows the diagram, and throwing away somebody's pan and zoom every
   * time they open a branch makes the canvas feel like it is fighting them.
   * Absent, any change of size resets, which is the old behaviour.
   */
  readonly resetKey?: string | undefined
  /** Where the root card is, so a diagram too big to fit opens on it rather
   *  than on a fitted overview nobody can read (`openingView`). */
  readonly root?: { readonly x: number; readonly y: number; readonly height: number } | undefined
}

export function usePanZoom(
  size: { readonly width: number; readonly height: number },
  options: PanZoomOptions = {},
): PanZoom {
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 })
  const [panning, setPanning] = useState(false)

  const frame = useRef<HTMLDivElement | null>(null)
  const gesture = useRef<Gesture>(NO_GESTURE)
  /** The size to fit to, remembered so the Fit button does not need an event. */
  const sizeRef = useRef(size)
  sizeRef.current = size
  const rootRef = useRef(options.root)
  rootRef.current = options.root

  const viewportOf = (el: HTMLDivElement): { width: number; height: number } => ({
    width: el.clientWidth,
    height: el.clientHeight,
  })

  const fit = useCallback(() => {
    const el = frame.current
    if (el === null) return
    setView(fitView(sizeRef.current, viewportOf(el)))
  }, [])

  /** Where a fresh diagram opens: fitted, unless that is too small to read. */
  const open = useCallback(() => {
    const el = frame.current
    if (el === null) return
    const root = rootRef.current
    setView(
      root === undefined
        ? fitView(sizeRef.current, viewportOf(el))
        : openingView(sizeRef.current, viewportOf(el), root),
    )
  }, [])

  /*
   * Open afresh when the diagram is a different diagram. Not on every render:
   * that would cancel a pan the moment React re-rendered for any other reason.
   * Without a `resetKey` the size stands in for one, as it always did.
   */
  const resetOn = options.resetKey ?? `${size.width}x${size.height}`
  useEffect(() => {
    open()
  }, [open, resetOn])

  /*
   * When the *frame* changes size — a phone rotated, an inspector opened beside
   * it — keep whatever was in the middle in the middle. Refitting instead would
   * throw away the person's place for something they did not ask for.
   * `ResizeObserver` fires once on observe; the opening view above covers that.
   */
  useEffect(() => {
    const el = frame.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    let last = viewportOf(el)
    const observer = new ResizeObserver(() => {
      const now = viewportOf(el)
      const dx = (now.width - last.width) / 2
      const dy = (now.height - last.height) / 2
      last = now
      if (dx !== 0 || dy !== 0) setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const frameRef = useCallback((el: HTMLDivElement | null) => {
    frame.current = el
  }, [])

  // The wheel, attached by hand so it can be non-passive. See the note above.
  useEffect(() => {
    const el = frame.current
    if (el === null) return
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault()
      const box = el.getBoundingClientRect()
      const origin = { x: e.clientX - box.left, y: e.clientY - box.top }
      // A trackpad reports small deltas often and a mouse wheel large ones
      // rarely; exponentiating makes both feel like the same gesture.
      setView((v) => zoomAbout(v, Math.exp(-e.deltaY / 320), origin))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const localPoint = (e: React.PointerEvent<HTMLDivElement>): { x: number; y: number } => {
    const box = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - box.left, y: e.clientY - box.top }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Not a secondary mouse button. A touch has no meaningful `button`.
    if (e.button !== 0 && e.pointerType === 'mouse') return
    gesture.current = begin(gesture.current, e.pointerId, localPoint(e))
    // Deliberately no `setPointerCapture` here — see the note at the top.
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const step = move(gesture.current, e.pointerId, localPoint(e))
    gesture.current = step.gesture

    if (step.capture) {
      // Now it is a drag, so keep it alive if the finger leaves the box. By
      // this point no click is owed to anything underneath.
      e.currentTarget.setPointerCapture(e.pointerId)
      setPanning(true)
    }
    if (step.pan) {
      const { dx, dy } = step.pan
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }))
    }
    if (step.zoom) {
      const { factor, origin } = step.zoom
      setView((v) => zoomAbout(v, factor, origin))
    }
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    gesture.current = end(gesture.current, e.pointerId)
    if (gesture.current.points.size === 0) setPanning(false)
  }

  return {
    view,
    frameRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    panning,
    tapped: () => wasTap(gesture.current),
    zoomBy: (factor: number) => {
      const el = frame.current
      const origin =
        el === null ? { x: 0, y: 0 } : { x: el.clientWidth / 2, y: el.clientHeight / 2 }
      setView((v) => zoomAbout(v, factor, origin))
    },
    fit,
    reveal: (box, insetRight = 0) => {
      const el = frame.current
      if (el === null) return
      const { width, height } = viewportOf(el)
      setView((v) => revealBox(v, box, { width: Math.max(width - insetRight, 1), height }))
    },
  }
}
